/**
 * ImpersonationBanner — Phase 10P.
 *
 * Persistent banner shown across every page while the platform owner is
 * acting as a tenant. Reads state from ImpersonationContext.
 *
 * Hidden when no impersonation is active. Renders on top of any other
 * banner (PlatformModeBanner / TenantStatusBanner) — those are suppressed
 * by AppShell when impersonating to keep the operator focused.
 */
import React from "react";
import { LifeBuoy, LogOut, Loader2 } from "lucide-react";
import { useImpersonation } from "@/context/ImpersonationContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";


export default function ImpersonationBanner() {
  const { isImpersonating, impersonation, stopImpersonation, stopping } = useImpersonation();
  const { tenant } = useAuth();

  if (!isImpersonating) return null;

  const tenantName = tenant?.business_name
    || impersonation?.target_tenant_id
    || "tenant";

  return (
    <div
      className="bg-gradient-to-r from-[#7C2D12] via-[#92400E] to-[#7C2D12] text-white px-4 py-2 text-xs sticky top-0 z-50 shadow-[0_2px_8px_rgba(0,0,0,0.18)]"
      role="alert"
      data-testid="impersonation-banner"
      data-actor={impersonation?.actor_email || ""}
      data-tenant={impersonation?.target_tenant_id || ""}
    >
      <div className="max-w-[1400px] mx-auto flex items-center gap-3 flex-wrap sm:flex-nowrap">
        <span className="inline-flex items-center gap-1.5 shrink-0">
          <LifeBuoy className="w-3.5 h-3.5" />
          <span className="uppercase tracking-widest text-[10px] font-semibold">
            Modalità assistenza
          </span>
        </span>
        <div className="flex-1 min-w-0 text-[12px] leading-tight">
          Stai visualizzando questo account come{" "}
          <strong className="font-semibold">{tenantName}</strong>
          <span className="opacity-80 ml-2 text-[11px] hidden sm:inline">
            · operatore: {impersonation?.actor_email || "—"}
          </span>
        </div>
        <Button
          size="sm"
          variant="outline"
          disabled={stopping}
          onClick={() => stopImpersonation()}
          className="h-7 px-3 bg-white/15 hover:bg-white/25 border-white/40 text-white text-xs shrink-0"
          data-testid="impersonation-exit-btn"
        >
          {stopping ? (
            <Loader2 className="w-3 h-3 mr-1.5 animate-spin" />
          ) : (
            <LogOut className="w-3 h-3 mr-1.5" />
          )}
          Esci da impersonation
        </Button>
      </div>
    </div>
  );
}
